import { message, notification } from "antd";
import i18next from "i18next";
import { ofType } from "redux-observable";
import { concat, EMPTY, of } from "rxjs";
import { catchError, switchMap, filter, tap, map, mergeMap } from "rxjs/operators";
import { DriveService } from "../../services/DriveService";
import { startLoading, stopLoading } from "@/store/loading";
import type { RootEpic } from "@/store/types";
import { driveActions } from "./driveSlice";
import { navigationActions } from "@/store/navigation";

/**
 * EPIC 1: Check Company Root
 * Responsibility: Verify if company root exists, if not found trigger creation
 */
export const checkCompanyRootEpic$: RootEpic = (action$) =>
    action$.pipe(
        ofType(driveActions.checkCompanyRootRequest.type),
        switchMap((action: any) => {
            const { companyId, orgId } = action.payload;

            return concat(
                of(startLoading({ key: "checkCompanyRoot" })),
                DriveService.checkUserRoot(orgId).pipe(
                    switchMap((companyRootId) => {
                        // Check if companyRootId exists and is valid
                        if (companyRootId && companyRootId.trim()) {
                            // console.log("✓ Company root found:", companyRootId);
                            return [
                                driveActions.checkCompanyRootSuccess({ companyRootId }),
                            ];
                        } else {
                            // Company root not found, return failure to trigger creation
                            // console.log("✗ Company root not found, needs creation");
                            return [
                                driveActions.checkCompanyRootFailure(
                                    "Company root not found"
                                ),
                            ];
                        }
                    }),
                    catchError((err) => {
                        console.error("Error checking company root:", err);
                        // If error, also trigger creation
                        return of(
                            driveActions.checkCompanyRootFailure(
                                err.message || "Error checking company root"
                            )
                        );
                    })
                ),
                of(stopLoading({ key: "checkCompanyRoot" }))
            );
        })
    );

/**
 * EPIC 2: Create Company Root (triggered by checkCompanyRootFailure)
 * Responsibility: Create company root folder when it doesn't exist
 */
export const createCompanyRootEpic$: RootEpic = (action$) =>
    action$.pipe(
        ofType(driveActions.createCompanyRootRequest.type),
        switchMap((action: any) => {
            const { orgId, companyName, companyEmail, companyId } = action.payload;

            const createData = {
                name: `root${orgId}`,
                type: "folder",
                createUser: {
                    id: orgId,
                    name: companyName,
                    icon: "",
                    email: companyEmail,
                },
            };

            return concat(
                of(startLoading({ key: "createCompanyRoot" })),
                DriveService.createCompanyRoot(orgId, companyId, createData).pipe(
                    switchMap((createRes: any) => {
                        const companyRootId = createRes?.id || createRes?._id || createRes;
                        // console.log("✓ Company root created:", companyRootId);

                        notification.success({
                            message: "Company Drive Initialized",
                            description: "Company workspace has been set up successfully.",
                        });

                        return [
                            driveActions.createCompanyRootSuccess({
                                companyRootId: String(companyRootId),
                            }),
                        ];
                    }),
                    catchError((err) => {
                        console.error("Error creating company root:", err);
                        return of(
                            driveActions.createCompanyRootFailure(
                                err.message || "Error creating company root"
                            )
                        );
                    })
                ),
                of(stopLoading({ key: "createCompanyRoot" }))
            );
        })
    );

/**
 * EPIC 3: Check User Root
 * Responsibility: Verify if user root exists, if not found trigger creation
 */
export const checkUserRootEpic$: RootEpic = (action$, state$) =>
    action$.pipe(
        ofType(driveActions.checkUserRootRequest.type),
        switchMap((action: any) => {
            const { userId, companyId } = action.payload;
            const orgId = localStorage.getItem("orgId") || "";

            // Call checkCompanyRoot simultaneously (fire and forget)
            // DriveService.checkCompanyRoot(companyId, orgId).subscribe();

            return concat(
                of(startLoading({ key: "checkUserRoot" })),
                DriveService.checkUserRoot(userId).pipe(
                    switchMap((userRootId) => {
                        // Check if userRootId exists and is valid
                        if (userRootId && userRootId.trim()) {
                            // console.log("✓ User root found:", userRootId);
                            return [driveActions.checkUserRootSuccess({ userRootId })];
                        } else {
                            // User root not found, return failure to trigger creation
                            // console.log("✗ User root not found, needs creation");
                            return [
                                driveActions.checkUserRootFailure("User root not found"),
                            ];
                        }
                    }),
                    catchError((err) => {
                        console.error("Error checking user root:", err);
                        // If error, also trigger creation
                        return of(
                            driveActions.checkUserRootFailure(
                                err.message || "Error checking user root"
                            )
                        );
                    })
                ),
                of(stopLoading({ key: "checkUserRoot" }))
            );
        })
    );

/**
 * EPIC 4: Create User Root (triggered by checkUserRootFailure)
 * Responsibility: Create user root folder when it doesn't exist
 */
export const createUserRootEpic$: RootEpic = (action$) =>
    action$.pipe(
        ofType(driveActions.createUserRootRequest.type),
        switchMap((action: any) => {
            const { userId, companyRootId, companyId } = action.payload;

            const createData = {
                name: `root${userId}`,
                type: "folder",
                parentId: companyRootId,
            };

            return concat(
                of(startLoading({ key: "createUserRoot" })),
                DriveService.createUserRoot(userId, companyId, createData).pipe(
                    switchMap((createRes: any) => {
                        const userRootId = createRes?.id || createRes?._id || createRes;
                        // console.log("✓ User root created:", userRootId);

                        notification.success({
                            message: "Welcome!",
                            description:
                                "Your workspace is empty. Start by uploading a file or creating a folder.",
                        });

                        return [
                            driveActions.createUserRootSuccess({
                                userRootId: String(userRootId),
                            }),
                        ];
                    }),
                    catchError((err) => {
                        console.error("Error creating user root:", err);
                        return of(
                            driveActions.createUserRootFailure(
                                err.message || "Error creating user root"
                            )
                        );
                    })
                ),
                of(stopLoading({ key: "createUserRoot" }))
            );
        })
    );

/**
 * EPIC 5: Load User Root Files
 * Responsibility: Load files from user root folder
 */
// export const loadUserRootFilesEpic$: RootEpic = (action$) =>
//     action$.pipe(
//         ofType(driveActions.loadUserRootFilesRequest.type),
//         switchMap((action: any) => {
//             const { userRootId } = action.payload;

//             return concat(
//                 of(startLoading({ key: "loadUserRootFiles" })),
//                 DriveService.getFilesByFolderRoot(userRootId).pipe(
//                     switchMap((files) => {
//                         // console.log("✓ Files loaded:", files?.length || 0);
//                         return [driveActions.loadUserRootFilesSuccess({ files: files || [] })];
//                     }),
//                     catchError((err) => {
//                         console.error("Error loading files:", err);
//                         return of(
//                             driveActions.loadUserRootFilesFailure(
//                                 err.message || "Error loading files"
//                             )
//                         );
//                     })
//                 ),
//                 of(stopLoading({ key: "loadUserRootFiles" }))
//             );
//         })
//     );

/**
 * ORCHESTRATOR EPIC: Init Drive Flow
 * Responsibility: Orchestrate the complete flow
 * 1. Check Company Root → Create if not exists
 * 2. Check User Root → Create if not exists
 * 3. Load User Root Files
 *
 * Flow communication via actions (no direct epic calls)
 */
export const initDriveFlowEpic$: RootEpic = (action$) =>
    action$.pipe(
        ofType(driveActions.initDriveFlowRequest.type),
        switchMap((action: any) => {
            const { companyId, orgId, companyName, companyEmail, userId } =
                action.payload;

            // Step 1: Start with checking company root
            return [driveActions.checkCompanyRootRequest({ companyId, orgId })];
        })
    );

/**
 * Flow Step 2: After Company Root Check Failure → Create Company Root
 */
export const onCompanyRootCheckFailureEpic$: RootEpic = (action$, state$) =>
    action$.pipe(
        ofType(driveActions.checkCompanyRootFailure.type),
        filter(() => state$.value.drive.flowStatus === "initializing"),
        switchMap(() => {
            const state = state$.value;
            const { companyId } = state.drive;

            // Get orgId from action$ or localStorage
            const orgId = localStorage.getItem("orgId") || "";
            const companyName = localStorage.getItem("companyName") || "";
            const companyEmail = localStorage.getItem("companyEmail") || "";
            const companyIdLocal = localStorage.getItem("companyId") || "";

            // console.log("→ Triggering company root creation");
            return [
                driveActions.createCompanyRootRequest({
                    orgId,
                    companyName,
                    companyEmail,
                    companyId: companyIdLocal,
                }),
            ];
        })
    );

/**
 * Flow Step 3: After Company Root Success → Check User Root
 */
export const onCompanyRootSuccessEpic$: RootEpic = (action$, state$) =>
    action$.pipe(
        ofType(
            driveActions.checkCompanyRootSuccess.type,
            driveActions.createCompanyRootSuccess.type
        ),
        filter(() => state$.value.drive.flowStatus === "initializing"),
        switchMap((action: any) => {
            const companyRootId = action.payload.companyRootId;
            const userId = localStorage.getItem("projectId") || "";

            // console.log("→ Company root ready, checking user root");
            return [driveActions.checkUserRootRequest({ userId, companyRootId })];
        })
    );

/**
 * Flow Step 4: After User Root Check Failure → Create User Root
 */
export const onUserRootCheckFailureEpic$: RootEpic = (action$, state$) =>
    action$.pipe(
        ofType(driveActions.checkUserRootFailure.type),
        filter(() => state$.value.drive.flowStatus === "initializing"),
        switchMap(() => {
            const state = state$.value;
            const companyRootId = state.drive.companyRootId;
            const userId = localStorage.getItem("projectId") || "";
            const companyId = localStorage.getItem("companyId") || "";

            if (!companyRootId) {
                console.error("✗ Company root ID missing, cannot create user root");
                return [
                    driveActions.initDriveFlowFailure(
                        "Company root ID missing"
                    ),
                ];
            }

            // console.log("→ Triggering user root creation");
            return [
                driveActions.createUserRootRequest({
                    userId,
                    companyRootId,
                    companyId,
                }),
            ];
        })
    );

/**
 * Flow Step 5: After User Root Success → Load Files
 */
export const onUserRootSuccessEpic$: RootEpic = (action$, state$) =>
    action$.pipe(
        ofType(
            driveActions.checkUserRootSuccess.type,
            driveActions.createUserRootSuccess.type
        ),
        filter(() => state$.value.drive.flowStatus === "initializing"),
        switchMap((action: any) => {
            const userRootId = action.payload.userRootId;

            // console.log("→ User root ready, loading files");
            return [driveActions.loadUserRootFilesRequest({ userRootId })];
        })
    );

/**
 * Flow Step 6: After Files Loaded → Complete Flow
 */
export const onFilesLoadedEpic$: RootEpic = (action$, state$) =>
    action$.pipe(
        ofType(driveActions.loadUserRootFilesRequest.type),
        filter(() => state$.value.drive.flowStatus === "initializing"),
        switchMap(() => {
            const successMsg =
                i18next?.t?.("login:login_success") || "Login success!";
            message.success(successMsg);
            // console.log("✓ Drive flow completed successfully");
            return [driveActions.initDriveFlowSuccess()];
        })
    );

/**
 * Flow Error Handler: Handle any failure during flow
 */
export const onFlowErrorEpic$: RootEpic = (action$, state$) =>
    action$.pipe(
        ofType(
            driveActions.createCompanyRootFailure.type,
            driveActions.createUserRootFailure.type,
            driveActions.loadUserRootFilesFailure.type
        ),
        filter(() => state$.value.drive.flowStatus === "initializing"),
        switchMap((action: any) => {
            console.error("✗ Drive flow failed:", action.payload);
            return [driveActions.initDriveFlowFailure(action.payload)];
        })
    );

export const persistUserRootEpic: RootEpic = (action$) =>
    action$.pipe(
        ofType(
            driveActions.createUserRootSuccess.type,
            driveActions.checkUserRootSuccess.type
        ),
        tap((action: ReturnType<
            | typeof driveActions.createUserRootSuccess
            | typeof driveActions.checkUserRootSuccess
        >) => {
            const { userRootId } = action.payload;

            localStorage.setItem("userRootId", userRootId);
            localStorage.setItem("folderRootId", userRootId);
            localStorage.setItem("folderId", userRootId);
        }),
        mergeMap((action) => {
            const { userRootId } = action.payload;

            return of(
                navigationActions.setFolderRootId(userRootId),
                navigationActions.setCurrentFolderId(userRootId)
            );
        })
    );

/**
 * Export all drive epics
 */
export const driveEpics = [
    // Individual epics
    checkCompanyRootEpic$,
    createCompanyRootEpic$,
    checkUserRootEpic$,
    createUserRootEpic$,
    // loadUserRootFilesEpic$,

    // Flow orchestrator
    initDriveFlowEpic$,

    // Flow transition epics
    onCompanyRootCheckFailureEpic$,
    onCompanyRootSuccessEpic$,
    onUserRootCheckFailureEpic$,
    onUserRootSuccessEpic$,
    onFilesLoadedEpic$,
    onFlowErrorEpic$,
    persistUserRootEpic,
];
